import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../store";
import axios from "axios";

interface ItemDetailsState {
  phoneNumber: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: ItemDetailsState = {
  phoneNumber: null,
  loading: false,
  error: null,
};

// Async thunk for fetching item details (phone number)
export const fetchItemDetails = createAsyncThunk(
  "itemDetails/fetchItemDetails",
  async (
    { accountId, itemId }: { accountId: string; itemId: string },
    { rejectWithValue, getState }
  ) => {
    try {
      const state = getState() as RootState;
      const token = state.auth.token;

      const response = await axios.get(
        `https://api.insightssystem.com/api:B4vX-Ohe/monday/chat/item/phone?accountId=${accountId}&itemId=${itemId}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: token })
          }
        }
      );

      if (!response.data) {
        throw new Error(`Failed to fetch item details: ${response.status}`);
      }

      return response.data.data || response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(
          error.response?.data?.message || 
          error.message || 
          "Failed to fetch item details"
        );
      }
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to fetch item details"
      );
    }
  }
);

const itemDetailsSlice = createSlice({
  name: "itemDetails",
  initialState,
  reducers: {
    clearItemDetails: (state) => {
      state.phoneNumber = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchItemDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchItemDetails.fulfilled, (state, action) => {
        state.loading = false;
        // Extract phone number from response
        if (action.payload?.phoneNumber && typeof action.payload.phoneNumber === 'string') {
          state.phoneNumber = action.payload.phoneNumber;
        } else if (action.payload?.phone && typeof action.payload.phone === 'string') {
          state.phoneNumber = action.payload.phone;
        } else {
          state.phoneNumber = null;
        }
      })
      .addCase(fetchItemDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearItemDetails } = itemDetailsSlice.actions;

// Selectors
export const selectItemPhoneNumber = (state: RootState) => state.itemDetails.phoneNumber;
export const selectItemDetailsLoading = (state: RootState) => state.itemDetails.loading;
export const selectItemDetailsError = (state: RootState) => state.itemDetails.error;

export default itemDetailsSlice.reducer;

